/**
 * F17.2.13 — Dialog "Aggiungi controsoffitto al preventivo".
 *
 * Variante del wall AddToEstimateDialogV2 con questi cambi sostanziali:
 *
 *  1. Niente "altezza parete" (irrilevante per ceiling)
 *  2. Niente "livello finitura Q1-Q4" (i livelli sono pensati per pareti — il
 *     ceiling avrà un livello specifico se servirà in futuro)
 *  3. **Perimetro stanza** (m) + **profilo perimetrale** (materiale) →
 *     calcolano un costo FISSO per stanza che si somma al totale (NON moltiplicato per area)
 *  4. Pre-fill perimetro = 4·√area (override manuale per L-shape / stanze allungate)
 *
 * Formula totale:
 *    totalCost = (cost_per_sqm × area) + (perimeter_profile €/ml × perimeter_m × 1.05 sfrido)
 *
 * Il dialog scrive su `estimate_stratigraphies` via `useCreateEstimateStratigraphy`
 * (le colonne `perimeter_m` e `perimeter_profile_cost` esistono già nel DB).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Lock, AlertTriangle, FilePlus2, Square, Anchor, Calculator } from 'lucide-react';
import EstimateSelectField from './EstimateSelectField';
import { toast } from 'sonner';
import { useEstimates } from '@/hooks/useEstimates';
import { useCreateEstimateStratigraphy } from '@/hooks/useCreateEstimateStratigraphy';
import { useEstimateStratigraphiesQuery } from '@/hooks/useEstimateStratigraphiesQuery';
import { useMaterials, type DatabaseMaterial } from '@/hooks/useMaterials';
import {
  computePerimeterProfileCost,
  estimatePerimeterFromArea,
} from '@/components/configurator-v2/hooks/computeCeilingCosts';
import { formatNumber } from '@/utils/format';

interface AddCeilingToEstimateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ID stratigrafia ceiling salvata. Senza ID il dialog mostra alert "salva prima". */
  stratigraphyId: string | null;
  stratigraphyName: string;
  stratigraphyDescription?: string;
  /** Costo €/m² calcolato del controsoffitto (esclude perimetro). */
  costPerSqm: number;
  /** Preselezione preventivo (es. quando si è entrati con ?estimate=...). */
  preselectedEstimateId?: string | null;
  onAdded?: (estimateId: string) => void;
}

const AddCeilingToEstimateDialog: React.FC<AddCeilingToEstimateDialogProps> = ({
  open,
  onOpenChange,
  stratigraphyId,
  stratigraphyName,
  stratigraphyDescription,
  costPerSqm,
  preselectedEstimateId,
  onAdded,
}) => {
  const { estimates } = useEstimates();
  const { createEstimateStratigraphy, isCreating } = useCreateEstimateStratigraphy();
  const { data: materials = [] } = useMaterials();

  const [estimateId, setEstimateId] = useState<string>('');
  const [name, setName] = useState(stratigraphyName);
  const [description, setDescription] = useState(stratigraphyDescription ?? '');
  const [area, setArea] = useState<number>(20);
  const [perimeterM, setPerimeterM] = useState<number>(estimatePerimeterFromArea(20));
  const [perimeterAuto, setPerimeterAuto] = useState<boolean>(true);
  const [perimeterProfileId, setPerimeterProfileId] = useState<string>('');
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  const { estimateStratigraphies } = useEstimateStratigraphiesQuery(estimateId);

  // ===== Profili perimetrali (categoria structure_guide) =====
  // Le guide a U sono i profili perimetrali usati anche al bordo del controsoffitto.
  const perimeterProfiles = useMemo(() => {
    return materials.filter((m) => m.category === 'structure_guide');
  }, [materials]);

  // Reset quando si apre
  useEffect(() => {
    if (!open) return;
    setName(stratigraphyName);
    setDescription(stratigraphyDescription ?? '');
    setArea(20);
    setPerimeterM(estimatePerimeterFromArea(20));
    setPerimeterAuto(true);
    setShowDuplicateWarning(false);
    if (preselectedEstimateId) setEstimateId(preselectedEstimateId);
  }, [open, stratigraphyName, stratigraphyDescription, preselectedEstimateId]);

  // Auto-update perimeter quando l'utente modifica area (se perimeterAuto)
  useEffect(() => {
    if (perimeterAuto) {
      setPerimeterM(estimatePerimeterFromArea(area));
    }
  }, [area, perimeterAuto]);

  const selectedEstimate = useMemo(
    () => estimates.find((e: any) => e.id === estimateId),
    [estimates, estimateId],
  );
  const isContracted = selectedEstimate?.status === 'contracted';
  const isDuplicate = useMemo(() => {
    if (!estimateId || !stratigraphyId) return false;
    return (estimateStratigraphies ?? []).some(
      (es: any) => es.stratigraphyId === stratigraphyId,
    );
  }, [estimateStratigraphies, estimateId, stratigraphyId]);

  // ===== Calcolo costo perimetro =====
  const selectedProfile: DatabaseMaterial | null = useMemo(() => {
    if (!perimeterProfileId) return null;
    return materials.find((m) => m.id === perimeterProfileId) ?? null;
  }, [materials, perimeterProfileId]);

  const perimeterBreakdown = useMemo(
    () => computePerimeterProfileCost(perimeterM, selectedProfile),
    [perimeterM, selectedProfile],
  );

  // ===== Totali =====
  const areaCost = Math.round(costPerSqm * area * 100) / 100;
  const totalCost = Math.round((areaCost + perimeterBreakdown.cost) * 100) / 100;

  const submit = () => {
    if (!stratigraphyId) {
      toast.error('Salva il controsoffitto prima di aggiungerlo a un preventivo.');
      return;
    }
    if (!estimateId) {
      toast.error('Seleziona un preventivo.');
      return;
    }
    if (isContracted) {
      toast.error('Preventivo contrattualizzato: non si possono aggiungere controsoffitti.');
      return;
    }
    if (isDuplicate && !showDuplicateWarning) {
      setShowDuplicateWarning(true);
      return;
    }

    createEstimateStratigraphy(
      {
        estimateId,
        stratigraphyId,
        name: name.trim() || stratigraphyName,
        description: description.trim() || undefined,
        area,
        quantity: 1,
        unitCost: costPerSqm,
        totalCost,
        perimeterM,
        perimeterProfileCost: perimeterBreakdown.cost,
        perimeterProfileId: selectedProfile?.id ?? undefined,
      },
      {
        onSuccess: () => {
          toast.success('Controsoffitto aggiunto al preventivo.');
          onAdded?.(estimateId);
          onOpenChange(false);
        },
        onError: (err: unknown) => {
          // Override del toast generico del hook: mostriamo il messaggio reale
          // per debug (es. CHECK constraint, FK, RLS denial).
          const msg = err instanceof Error ? err.message : String(err);
          console.error('[AddCeilingToEstimate] errore real:', err);
          toast.error(`Errore: ${msg}`, { duration: 8000 });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FilePlus2 className="h-5 w-5 text-primary" />
            Aggiungi controsoffitto al preventivo
          </DialogTitle>
          <DialogDescription>
            Inserisci dimensioni della stanza (area + perimetro) e scegli il profilo
            perimetrale a U per il calcolo completo del costo.
          </DialogDescription>
        </DialogHeader>

        {!stratigraphyId ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Devi <strong>salvare il controsoffitto</strong> prima di poterlo aggiungere a un preventivo.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            {/* === Preventivo (filtrato: nasconde chiusi di default, badge colorati) === */}
            <EstimateSelectField
              estimates={estimates ?? []}
              value={estimateId}
              onChange={setEstimateId}
            />

            {isContracted && (
              <Alert variant="destructive">
                <Lock className="h-4 w-4" />
                <AlertDescription>
                  Preventivo <strong>contrattualizzato</strong>: non puoi aggiungere controsoffitti.
                </AlertDescription>
              </Alert>
            )}

            {showDuplicateWarning && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Controsoffitto <strong>già presente</strong> in questo preventivo. Clicca di nuovo "Conferma"
                  per aggiungerlo comunque (es. stanza diversa).
                </AlertDescription>
              </Alert>
            )}

            {/* === Nome + descrizione === */}
            <div className="space-y-1.5">
              <Label htmlFor="name">Nome (per questo preventivo)</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={stratigraphyName}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="desc">Note</Label>
              <Textarea
                id="desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Es. sala riunioni 2° piano"
              />
            </div>

            {/* === Dimensioni stanza === */}
            <div className="rounded-md border p-3 bg-zinc-50/30 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Square className="h-4 w-4 text-zinc-600" />
                <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                  Dimensioni stanza
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="area">Superficie (m²)</Label>
                  <Input
                    id="area"
                    type="number"
                    step="0.1"
                    min="0"
                    value={area}
                    onChange={(e) => setArea(Math.max(0, Number(e.target.value)))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="perimeter" className="flex items-center justify-between">
                    <span>Perimetro (m)</span>
                    <button
                      type="button"
                      onClick={() => setPerimeterAuto(!perimeterAuto)}
                      className="text-[10px] text-primary hover:underline"
                    >
                      {perimeterAuto ? 'Auto' : 'Manuale'}
                    </button>
                  </Label>
                  <Input
                    id="perimeter"
                    type="number"
                    step="0.1"
                    min="0"
                    value={perimeterM}
                    onChange={(e) => {
                      setPerimeterAuto(false);
                      setPerimeterM(Math.max(0, Number(e.target.value)));
                    }}
                    className={perimeterAuto ? 'text-zinc-500' : ''}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    {perimeterAuto
                      ? `Stima quadrata: 4·√area = ${formatNumber(perimeterM)} m`
                      : 'Override manuale (es. stanze L-shape)'}
                  </p>
                </div>
              </div>
            </div>

            {/* === Profilo perimetrale === */}
            <div className="space-y-1.5">
              <Label htmlFor="perim-prof" className="flex items-center gap-2">
                <Anchor className="h-3.5 w-3.5" />
                Profilo perimetrale a U
              </Label>
              <Select value={perimeterProfileId} onValueChange={setPerimeterProfileId}>
                <SelectTrigger id="perim-prof">
                  <SelectValue placeholder={
                    perimeterProfiles.length === 0
                      ? 'Nessun profilo guida disponibile'
                      : 'Seleziona un profilo a U…'
                  } />
                </SelectTrigger>
                <SelectContent>
                  {perimeterProfiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="truncate">{p.name}</span>
                        {p.width && (
                          <span className="text-[10px] text-muted-foreground shrink-0">
                            {p.width}mm
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground shrink-0 ml-auto font-mono">
                          €{formatNumber(p.net_price ?? p.unit_price ?? 0)}/ml
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedProfile && (
                <p className="text-[10px] text-muted-foreground">
                  Fabbisogno con sfrido 5%: <span className="font-mono font-medium">{formatNumber(perimeterBreakdown.mlNeeded)} ml</span>
                </p>
              )}
            </div>

            {/* === Preview costi === */}
            <div className="rounded-md border bg-emerald-50/40 p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-zinc-500">Pacchetto controsoffitto</span>
                <span className="font-mono">€{formatNumber(costPerSqm)} /m² × {formatNumber(area)} m²</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">  → costo pacchetto</span>
                <span className="font-mono font-medium">€{formatNumber(areaCost)}</span>
              </div>
              {perimeterBreakdown.cost > 0 && (
                <>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Profilo perimetrale</span>
                    <span className="font-mono">€{formatNumber(selectedProfile?.net_price ?? selectedProfile?.unit_price ?? 0)} /ml × {formatNumber(perimeterBreakdown.mlNeeded)} ml</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">  → costo perimetro</span>
                    <span className="font-mono font-medium">€{formatNumber(perimeterBreakdown.cost)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between font-bold pt-2 border-t border-emerald-200/70 text-base">
                <span className="flex items-center gap-1"><Calculator className="h-4 w-4" />Totale stanza</span>
                <span className="font-mono">€{formatNumber(totalCost)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button onClick={submit} disabled={!stratigraphyId || isCreating || isContracted}>
            {isCreating ? 'Aggiungo…' : showDuplicateWarning ? 'Aggiungi comunque' : 'Aggiungi al preventivo'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddCeilingToEstimateDialog;
